import type { Program } from '@compass/client/types/program';
import { ChevronsUpDown } from 'lucide-react';
import React, { useMemo, useState } from 'react';

import { cn } from '@/lib/utils';

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';

interface ProgramComboboxProps {
  programs: Program[];
  selectedProgramId: string | null;
  onProgramChange: (programId: string) => void;
  disabled?: boolean;
}

/**
 * Searchable program dropdown for the header
 * Uses Command component for fuzzy search and keyboard navigation
 * Shows search input only when there are 5 or more programs
 */
export function ProgramCombobox({
  programs,
  selectedProgramId,
  onProgramChange,
  disabled = false,
}: ProgramComboboxProps) {
  const [open, setOpen] = useState(false);

  const selectedProgram = useMemo(
    () => programs.find(p => p.id === selectedProgramId),
    [programs, selectedProgramId],
  );

  const showSearch = programs.length >= 5;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild disabled={disabled}>
        <button
          data-testid="program-switcher"
          role="combobox"
          aria-expanded={open}
          className={cn(
            'h-6 px-1 border-none inline-flex items-center gap-1',
            'focus:ring-0 focus:ring-offset-0 focus:outline-hidden',
            'text-sm text-foreground',
            disabled && 'opacity-50 cursor-not-allowed',
          )}
        >
          <span>{selectedProgram?.name || 'Select Program'}</span>
          <ChevronsUpDown className="h-4 w-4 text-muted-foreground shrink-0 hover:bg-muted p-0.5 rounded-sm" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[300px]" align="start">
        <Command>
          {showSearch && <CommandInput placeholder="Search programs..." className="h-9" />}
          <CommandList>
            <CommandEmpty>No programs found</CommandEmpty>
            <CommandGroup>
              {programs.map(program => (
                <CommandItem
                  key={program.id}
                  value={program.name}
                  onSelect={() => {
                    if (program.id) {
                      onProgramChange(program.id);
                      setOpen(false);
                    }
                  }}
                >
                  <div className="flex items-center justify-between w-full">
                    <span>{program.name}</span>
                    {program.id === selectedProgramId && (
                      <span className="text-xs text-muted-foreground">✓</span>
                    )}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
